import ApiService, { IRequestParameters } from '@/plugins/api-plugin/services/api.service'
import OrderStatusDao from '@/interfaces/order/OrderStatus.dao'
import { PageNumberPagination } from '@/interfaces/common/PageNumberPagination'
import ExportStatusDao from '@/interfaces/order/ExportStatus.dao'
import OrdersDao from '@/interfaces/order/Orders.dao'
import OrderNoteDao from '@/interfaces/order/OrderNote.dao'
import OrderSearchSummariesDao from '@/interfaces/order/OrderSearchSummaries.dao'
import OperationCentreDao from '@/interfaces/order/OperationsCentre.dao'
import ClientDao from '@/interfaces/order/Client.dao'
import CourierDao from '@/interfaces/order/Courier.dao'
import FulfillmentChannelDao from '@/interfaces/order/FulfillmentChannel.dao'
import SalesChannelDao from '@/interfaces/order/SalesChannel.dao'
import OrderLineItemsDao from '@/interfaces/order/OrderLineItems.dao'
import MaterialDao from '@/interfaces/order/Material.dao'
import RemittancesDao from '@/interfaces/order/Remittances.dao'
import RemittanceDao from '@/interfaces/order/Remittance.dao'
import RemittanceRequestsDao from '@/interfaces/order/RemittanceRequests.dao'
import RemittanceRequestLinesDao from '@/interfaces/order/RemittanceRequestLines.dao'
import { AxiosResponse } from 'axios'
import RemittancesMetadataDao from '@/interfaces/order/RemittancesMetadata.dao'
import OrderLatestDao from '@/interfaces/order/OrderLatest.dao'

export default class OrderService extends ApiService {
    async getOrderStatuses(params?: IRequestParameters): Promise<[OrderStatusDao[], PageNumberPagination] | null> {
        return this.request<undefined, [OrderStatusDao[], PageNumberPagination]>(
            'GET',
            '/orders/statuses',
            params,
            undefined,
            undefined,
            'getOrderStatuses',
        ).catch(() => null)
    }
    async getExportStatuses(params?: IRequestParameters): Promise<[ExportStatusDao[], PageNumberPagination] | null> {
        return this.request<undefined, [ExportStatusDao[], PageNumberPagination]>(
            'GET',
            '/orders/export-statuses',
            params,
            undefined,
            undefined,
            'getExportStatuses',
        ).catch(() => null)
    }
    async getOperationCentres(
        params?: IRequestParameters,
    ): Promise<[OperationCentreDao[], PageNumberPagination] | null> {
        return this.request<undefined, [OperationCentreDao[], PageNumberPagination]>(
            'GET',
            '/orders/operation-centres',
            params,
            undefined,
            undefined,
            'getOperationCentres',
        ).catch(() => null)
    }
    async getOrders(params?: IRequestParameters): Promise<[OrdersDao[], PageNumberPagination] | null> {
        return this.request<undefined, [OrdersDao[], PageNumberPagination]>(
            'GET',
            '/orders',
            params,
            undefined,
            undefined,
            'getOrders',
        ).catch(() => null)
    }
    async getOrdersLatest(params?: IRequestParameters): Promise<[OrderLatestDao[], PageNumberPagination] | null> {
        return this.request<undefined, [OrderLatestDao[], PageNumberPagination]>('GET', '/orders/latest', params).catch(
            () => null,
        )
    }
    async getOrderNotes(params?: IRequestParameters): Promise<[OrderNoteDao[], PageNumberPagination] | null> {
        return this.request<undefined, [OrderNoteDao[], PageNumberPagination]>(
            'GET',
            '/orders/order-notes',
            params,
            undefined,
            undefined,
            'getOrderNotes',
        ).catch(() => null)
    }
    async getOrderSearchSummaries(
        params?: IRequestParameters,
    ): Promise<[OrderSearchSummariesDao[], PageNumberPagination] | null> {
        return this.request<undefined, [OrderSearchSummariesDao[], PageNumberPagination]>(
            'GET',
            '/orders/order-search-summaries',
            params,
            undefined,
            undefined,
            'getOrderSearchSummaries',
        ).catch(() => null)
    }
    async getSuppliers(params?: IRequestParameters): Promise<[ClientDao[], PageNumberPagination] | null> {
        return this.request<undefined, [ClientDao[], PageNumberPagination]>(
            'GET',
            '/orders/suppliers',
            params,
            undefined,
            undefined,
            'getSuppliers',
        ).catch(() => null)
    }
    async getCouriers(params?: IRequestParameters): Promise<[CourierDao[], PageNumberPagination] | null> {
        return this.request<undefined, [CourierDao[], PageNumberPagination]>(
            'GET',
            '/orders/couriers',
            params,
            undefined,
            undefined,
            'getCouriers',
        ).catch(() => null)
    }
    async getFulfillmentChannels(
        params?: IRequestParameters,
    ): Promise<[FulfillmentChannelDao[], PageNumberPagination] | null> {
        return this.request<undefined, [FulfillmentChannelDao[], PageNumberPagination]>(
            'GET',
            '/orders/fulfillment-channels',
            params,
            undefined,
            undefined,
            'getFulfillmentChannels',
        ).catch(() => null)
    }
    async getOrderLineItems(params?: IRequestParameters): Promise<[OrderLineItemsDao[], PageNumberPagination] | null> {
        return this.request<undefined, [OrderLineItemsDao[], PageNumberPagination]>(
            'GET',
            '/orders/order-line-items',
            params,
            undefined,
            undefined,
            'getOrderLineItems',
        ).catch(() => null)
    }
    async getSalesChannels(params?: IRequestParameters): Promise<[SalesChannelDao[], PageNumberPagination] | null> {
        return this.request<undefined, [SalesChannelDao[], PageNumberPagination]>(
            'GET',
            '/orders/sales-channels',
            params,
            undefined,
            undefined,
            'getSalesChannels',
        ).catch(() => null)
    }

    async getMaterials(
        skus: string[],
        params?: IRequestParameters,
    ): Promise<[MaterialDao[], PageNumberPagination] | null> {
        return this.request<string[], [MaterialDao[], PageNumberPagination]>(
            'POST',
            '/orders/materials',
            params,
            skus,
            undefined,
            'getMaterials',
        ).catch(() => null)
    }

    async getMaterialsCompact(
        skus: string[],
        params?: IRequestParameters,
    ): Promise<[any[], PageNumberPagination] | null> {
        return this.request<string[], [any[], PageNumberPagination]>(
            'POST',
            '/orders/materials-compact',
            params,
            skus,
            undefined,
            'getMaterialsCompact',
        ).catch(() => null)
    }

    async importPurchaseOrders(importPurchaseOrders: any[]): Promise<any[] | null> {
        return this.request<any[], any[]>(
            'POST',
            '/orders/import-purchase-orders',
            undefined,
            importPurchaseOrders,
            undefined,
            'importPurchaseOrders',
        ).catch(() => null)
    }

    async importMaterialPrices(importMaterialPrices: any[]): Promise<any[] | null> {
        return this.request<any[], any[]>(
            'POST',
            '/orders/import-material-prices',
            undefined,
            importMaterialPrices,
            undefined,
            'importMaterialPrices',
        ).catch(() => null)
    }

    async importMaterials(importMaterials: any[]): Promise<any[] | null> {
        return this.request<any[], any[]>(
            'POST',
            '/orders/import-materials',
            undefined,
            importMaterials,
            undefined,
            'importMaterials',
        ).catch(() => null)
    }

    async getShippingCostCategories(params?: IRequestParameters): Promise<[any[], PageNumberPagination] | null> {
        return this.request<undefined, [any[], PageNumberPagination]>(
            'GET',
            '/orders/shipping-cost-categories',
            params,
            undefined,
            undefined,
            'getShippingCostCategories',
        ).catch(() => null)
    }

    async getChannelAccountTariffFlags(): Promise<any[] | null> {
        return this.request<undefined, any[]>(
            'GET',
            '/orders/channel-account-tariff-flags',
            undefined,
            undefined,
            undefined,
            'getChannelAccountTariffFlags',
        ).catch(() => null)
    }

    async getSalesChannelCategories(params?: IRequestParameters): Promise<[any[], PageNumberPagination] | null> {
        return this.request<undefined, [any[], PageNumberPagination]>(
            'GET',
            '/orders/sales-channel-categories',
            params,
            undefined,
            undefined,
            'getSalesChannelCategories',
        ).catch(() => null)
    }

    async getBundleAssociations(skus: string[]): Promise<any[] | null> {
        return this.request<string[], any[]>(
            'POST',
            '/orders/bundle-associations',
            undefined,
            skus,
            undefined,
            'getBundleAssociations',
        ).catch(() => null)
    }

    async importBundles(importBundles: any[]): Promise<any[] | null> {
        return this.request<any[], any[]>(
            'POST',
            '/orders/import-bundles',
            undefined,
            importBundles,
            undefined,
            'importBundles',
        ).catch(() => null)
    }

    async getRemittances(params?: IRequestParameters): Promise<RemittancesDao[] | null> {
        return this.request<undefined, RemittancesDao[]>(
            'GET',
            '/remittances',
            params,
            undefined,
            undefined,
            'getRemittances',
        ).catch(() => null)
    }

    async getRemittance(id: number): Promise<RemittanceDao | null> {
        return this.request<undefined, RemittanceDao>(
            'GET',
            `/remittances/${id}`,
            undefined,
            undefined,
            undefined,
            'getRemittance',
        ).catch(() => null)
    }

    async getRemittanceRequests(
        params?: IRequestParameters,
    ): Promise<[RemittanceRequestsDao[], PageNumberPagination] | null> {
        return this.request<undefined, [RemittanceRequestsDao[], PageNumberPagination]>(
            'GET',
            '/remittances/remittance-requests',
            params,
            undefined,
            undefined,
            'RemittanceRequestsDao',
        ).catch(() => null)
    }

    async getTransactionValueTotal(params?: IRequestParameters): Promise<number | null> {
        return this.request<undefined, number>(
            'GET',
            '/remittances/remittance-requests/transaction-value-total',
            params,
            undefined,
            undefined,
            'getTransactionValueTotal',
        ).catch(() => null)
    }

    async getRemittancesMetadata(params?: IRequestParameters): Promise<RemittancesMetadataDao | null> {
        return this.request<undefined, RemittancesMetadataDao>(
            'GET',
            '/remittances/metadata',
            params,
            undefined,
            undefined,
            'getRemittancesMetadata',
        ).catch(() => null)
    }

    async getRemittanceRequestLines(
        params?: IRequestParameters,
    ): Promise<[RemittanceRequestLinesDao[], PageNumberPagination] | null> {
        return this.request<undefined, [RemittanceRequestLinesDao[], PageNumberPagination]>(
            'GET',
            '/remittances/remittance-request-lines',
            params,
            undefined,
            undefined,
            'getRemittanceRequestLines',
        ).catch(() => null)
    }

    async approveRemittance(payload: { id: number; notes: string | null }): Promise<RemittanceDao | null> {
        return this.request<{ Id: number; Notes: string | null }, RemittanceDao>(
            'POST',
            '/remittances/approve-remittance',
            undefined,
            { Id: payload.id, Notes: payload.notes },
            undefined,
            'approveRemittance',
        ).catch(() => null)
    }

    async settleRemittance(payload: {
        id: number
        notes: string | null
        paymentRef: string
        paymentDate: Date
    }): Promise<RemittanceDao | null> {
        return this.request<{ Id: number; Notes: string | null; PaymentRef: string; PaymentDate: Date }, RemittanceDao>(
            'POST',
            '/remittances/settle-remittance',
            undefined,
            {
                Id: payload.id,
                Notes: payload.notes,
                PaymentRef: payload.paymentRef,
                PaymentDate: payload.paymentDate,
            },
            undefined,
            'settleRemittance',
        ).catch(() => null)
    }

    async deferRemittanceRequests(
        params: IRequestParameters,
        payload: {
            remittanceId: number
            notes: string
        },
    ): Promise<RemittanceRequestsDao[] | null> {
        return this.request<{ RemittanceId: number; Notes: string | null }, RemittanceRequestsDao[]>(
            'POST',
            '/remittances/defer-remittance-requests',
            params,
            {
                RemittanceId: payload.remittanceId,
                Notes: payload.notes,
            },
            undefined,
            'deferRemittanceRequests',
        ).catch(() => null)
    }

    async exportRemittanceRequests(params: IRequestParameters): Promise<AxiosResponse | null> {
        return this.request<undefined, AxiosResponse>(
            'POST',
            '/remittances/export-remittance-requests',
            params,
            undefined,
            'blob',
            'exportRemittanceRequests',
        ).catch(() => null)
    }

    async exportOrders(params: IRequestParameters): Promise<AxiosResponse | null> {
        return this.request<undefined, AxiosResponse>(
            'POST',
            '/orders/export-orders',
            params,
            undefined,
            'blob',
            'exportOrders',
        ).catch(() => null)
    }
}
